import type { RailBranch, RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "./runs";
import { markerRailPose } from "../../rail/RailPose/pose";
import { sceneStartMarkerCenterX } from "../LocationLayout/metrics";
import {
  branchIdForPartRun,
  partStaySegments,
} from "../../rail/PartRunPhase/phase";

export function branchForLocationRun(
  run: LocationRun,
  rail: RailLayout,
): RailBranch | undefined {
  if (run.runId.includes(":")) {
    const branchId = branchIdForPartRun(run);
    if (branchId === null) {
      return undefined;
    }
    return rail.branches.find((item) => item.id === branchId);
  }
  return branchForSiteRun(run, rail);
}

export function branchForSiteRun(
  run: LocationRun,
  rail: RailLayout,
): (typeof rail.branches)[number] | undefined {
  const direct = rail.branches.find((item) => item.id === run.runId);
  if (direct !== undefined) {
    return direct;
  }
  const matches = rail.branches.filter(
    (item) => item.label === run.locationLabel,
  );
  const covering = matches.find(
    (item) => run.startMs >= item.startMs && run.startMs < item.endMs,
  );
  if (covering !== undefined) {
    return covering;
  }
  return matches.find((item) => run.startMs < item.startMs) ?? matches[0];
}

/** Rail column where the traveler sits when a forked branch opens. */
export function branchForkMarkerCenterX(
  rail: RailLayout,
  branch: RailBranch,
  railMs: number,
  viewportWidth: number,
  totalDurationMs: number,
): number {
  const base = sceneStartMarkerCenterX(railMs, viewportWidth, totalDurationMs);
  const traveler = rail.stays.find(
    (stay) =>
      stay.rootPlaceId === branch.rootPlaceId && stay.startMs === branch.startMs,
  );
  if (traveler === undefined) {
    return base;
  }
  const pose = markerRailPose(traveler.characterId, branch.startMs, rail);
  return base + (pose?.dx ?? 0);
}

export function forkedBranchRailSlotX(
  rail: RailLayout,
  branch: RailBranch,
  railMs: number,
  viewportWidth: number,
  totalDurationMs: number,
): number | undefined {
  if ((branch.lane ?? 0) === 0 || railMs !== branch.startMs) {
    return undefined;
  }
  return branchForkMarkerCenterX(
    rail,
    branch,
    railMs,
    viewportWidth,
    totalDurationMs,
  );
}

export function forkTravelerCharacterId(
  rail: RailLayout,
  branch: RailBranch,
): string | undefined {
  return rail.stays.find(
    (stay) =>
      stay.rootPlaceId === branch.rootPlaceId
      && stay.startMs === branch.startMs,
  )?.characterId;
}

/** First visit on this part line (handoff at fork vs later rail column). */
export function partOpensAtBranchFork(
  rail: RailLayout,
  branch: RailBranch,
  run: LocationRun,
): boolean {
  const segments = partStaySegments(rail, run);
  const firstStart = segments[0]?.startMs ?? run.startMs;
  return firstStart === branch.startMs;
}
